import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Booking } from "../types/booking.entity";
import { FaExternalLinkAlt } from "react-icons/fa";
import { appendSearchToPath } from "src/customHooks/usePaginatedTableSearchParams";

type Props = { data: (Booking & { id: string })[] };

const statusClass = (status: Booking["bookingStatus"]) => {
  switch (status) {
    case "ACCEPTED":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "CANCELLED":
      return "bg-red-100 text-red-700 border-red-200";
    case "REJECTED":
      return "bg-rose-100 text-rose-700 border-rose-200";
    case "COMPLETED":
      return "bg-blue-100 text-blue-700 border-blue-200";
    case "EXPIRED":
      return "bg-gray-100 text-gray-700 border-gray-200";
    default:
      return "bg-amber-100 text-amber-700 border-amber-200";
  }
};

const coachTypeClass = (coachType: Booking["coachType"]) =>
  coachType === "PRO"
    ? "bg-indigo-100 text-indigo-700 border-indigo-200"
    : "bg-orange-100 text-orange-700 border-orange-200";


const formatDate = (ts: any) => new Date(ts?.toDate?.() || ts).toLocaleDateString();
const formatTime = (ts: any) => new Date(ts?.toDate?.() || ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export const BookingsTable: React.FC<Props> = ({ data }) => {
  const { search } = useLocation();

  return (
    <div className="overflow-x-auto mb-6 -mx-2 sm:mx-0">
      <table className="min-w-[1200px] sm:min-w-full border border-gray-200 divide-y divide-gray-200 text-[11px] sm:text-xs">
        <thead className="bg-gray-900 text-white text-center text-[10px] sm:text-xs">
          <tr>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Booking</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Participants</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Status</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Coach Type</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Created</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Date</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Slot</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && (
            <tr>
              <td colSpan={8} className="text-center py-4 text-xl text-gray-500">No Bookings</td>
            </tr>
          )}
          {data.map((b) => (
            <tr key={b.id} className="text-center odd:bg-white even:bg-gray-50 hover:bg-gray-100">
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200 font-mono text-[11px] sm:text-xs" title={b.id}>{b.id}</td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                <div className="flex flex-col gap-2 min-w-[180px]">
                  {/* Coach Section */}
                  <Link
                    to={`/users/${b.coachId}`}
                    className="flex items-center gap-2 p-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 hover:shadow-md hover:from-blue-100 hover:to-indigo-100 hover:border-blue-300 transition-all duration-200 cursor-pointer group no-underline"
                    title={`View Coach: ${b.coachId}`}
                  >
                    <div className="flex items-center justify-center px-2 py-1 bg-gradient-to-r from-blue-500 to-indigo-500 rounded text-white text-[8px] sm:text-[9px] font-bold uppercase tracking-wide group-hover:from-blue-600 group-hover:to-indigo-600 transition-all duration-200">
                      Coach
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[9px] sm:text-[10px] font-mono font-bold text-blue-700 group-hover:text-blue-900 transition-colors duration-200 break-all">
                        {b.coachId}
                      </div>
                    </div>
                  </Link>
                  
                  {/* Learner Section */}
                  <Link
                    to={`/users/${b.learnerId}`}
                    className="flex items-center gap-2 p-2 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200 hover:shadow-md hover:from-emerald-100 hover:to-teal-100 hover:border-emerald-300 transition-all duration-200 cursor-pointer group no-underline"
                    title={`View Learner: ${b.learnerId}`}
                  >
                    <div className="flex items-center justify-center px-2 py-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded text-white text-[8px] sm:text-[9px] font-bold uppercase tracking-wide group-hover:from-emerald-600 group-hover:to-teal-600 transition-all duration-200">
                      Learner
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[9px] sm:text-[10px] font-mono font-bold text-emerald-700 group-hover:text-emerald-900 transition-colors duration-200 break-all">
                        {b.learnerId}
                      </div>
                    </div>
                  </Link>
                </div>
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                <span className={`px-2 py-1 rounded-full border text-xs ${statusClass(b.bookingStatus)}`}>{b.bookingStatus}</span>
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                <span className={`px-2 py-1 rounded-full border text-xs ${coachTypeClass(b.coachType)}`}>{b.coachType}</span>
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] sm:text-[11px] font-semibold text-gray-700">{formatDate(b.createdAt)}</span>
                  <span className="text-[9px] sm:text-[10px] text-gray-500">{formatTime(b.createdAt)}</span>
                </div>
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] sm:text-[11px] font-semibold text-gray-700">{formatDate(b.date)}</span>
                  <span className="text-[9px] sm:text-[10px] text-gray-500">{formatTime(b.date)}</span>
                </div>
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200" title={b.slotId}>
                <Link
                  to={`/slots/${b.slotId}`}
                  className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 transform hover:scale-110 will-change-transform no-underline font-mono font-semibold text-[10px] sm:text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
                >
                  {b.slotId}
                </Link>
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                <Link
                  to={appendSearchToPath(`/bookings/${b.id}`, search)}
                  className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-2"
                >
                  <FaExternalLinkAlt />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default BookingsTable;

